import { gatewayRequest, sign } from "@prostocheck/kep";
import {
  castErrorResult,
  createErrorResult,
  createErrorResultFromException,
  createSuccessResult,
  ERRORS,
  FISCAL_SERVER_URL,
  IFiscalClient,
  SimpleResult,
} from "@prostocheck/shared";

import {
  COMMAND_PATH,
  JSON_CONTENT_TYPE,
  RESULT_AS_JSON_QUERY,
  SIGNED_CONTENT_TYPE,
} from "./client.constants";
import { ICreateFiscalClientParams, IRunCommandParams } from "./client.models";
import { enqueue } from "./client.queue";
import {
  createRequestUid,
  isNoContent,
  isSuccess,
  toErrorKeyFromServer,
  toErrorKeyFromStatus,
} from "./client.utils";

export const createFiscalClient = ({
  keyBox,
  gatewayUrl,
  baseUrl,
  shouldTimestamp = true,
}: ICreateFiscalClientParams): IFiscalClient => ({
  keyBox,
  gatewayUrl,
  baseUrl: baseUrl ?? FISCAL_SERVER_URL,
  shouldTimestamp,
});

/**
 * Sends one command. A successful result carries the response JSON, or `null`
 * when ДПС answered "no such data" — which is a normal outcome, not a failure:
 * a register may simply have no shifts in the requested period.
 *
 * Signed commands carry no taxpayer id: ДПС reads it from the certificate that
 * signed the body. That is why reads need the key just as much as writes do.
 *
 * Serialised per client: the server refuses overlapping requests from the same
 * taxpayer — see `client.queue.ts`.
 */
export const runCommand = <TResponse>(
  params: IRunCommandParams,
): Promise<SimpleResult<TResponse | null>> =>
  enqueue(params.client, () => sendCommand<TResponse>(params));

const sendCommand = async <TResponse>({
  client,
  command,
  isSigned,
}: IRunCommandParams): Promise<SimpleResult<TResponse | null>> => {
  const payload = new TextEncoder().encode(
    JSON.stringify({ UID: createRequestUid(), ...command }),
  );

  let body: Uint8Array = payload;

  if (isSigned) {
    const signed = await sign({
      keyBox: client.keyBox,
      data: payload,
      shouldTimestamp: client.shouldTimestamp,
    });

    if (!signed.success || !signed.data) {
      return castErrorResult(signed);
    }

    body = signed.data;
  }

  try {
    const { status, body: responseBody } = await gatewayRequest({
      gatewayUrl: client.gatewayUrl,
      request: {
        url: `${client.baseUrl}${COMMAND_PATH}?${RESULT_AS_JSON_QUERY}`,
        body,
        contentType: isSigned ? SIGNED_CONTENT_TYPE : JSON_CONTENT_TYPE,
      },
    });

    return readResponse<TResponse>({ status, body: responseBody });
  } catch (requestError) {
    return createErrorResultFromException(
      requestError,
      ERRORS.fiscal.gatewayUnreachable,
    );
  }
};

const readResponse = <TResponse>({
  status,
  body,
}: {
  status: number;
  body: Uint8Array;
}): SimpleResult<TResponse | null> => {
  const text = new TextDecoder().decode(body);
  const parsed = toJson(text);
  const serverCode =
    typeof parsed?.ErrorCode === "number" ? parsed.ErrorCode : undefined;

  if (isNoContent({ status, serverCode })) {
    return createSuccessResult<TResponse | null>(null);
  }

  if (!isSuccess({ status, serverCode })) {
    // ДПС's own wording is Ukrainian regardless of the interface language, so
    // it goes to the log as a label and never onto the screen.
    const serverMessage =
      typeof parsed?.ErrorMessage === "string"
        ? parsed.ErrorMessage
        : text.slice(0, 200) || undefined;

    return createErrorResult(
      serverCode === undefined
        ? toErrorKeyFromStatus(status)
        : toErrorKeyFromServer(serverCode),
      serverMessage,
      { status, serverCode },
    );
  }

  return createSuccessResult<TResponse | null>(parsed as TResponse);
};

/**
 * A non-JSON body means the request never reached the command handler — a
 * gateway error page, or the plain-text error grammar that `resultAsJson`
 * was meant to replace. Either way there is nothing to parse.
 */
const toJson = (
  text: string,
): {
  ErrorCode?: unknown;
  ErrorMessage?: unknown;
} | null => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};
